<template>
  <div class="container mt-5">
    <div class="text-center verification-pending">
      <h2>Check Your Email</h2>
      <div class="email-icon-container mb-4">
        <svg class="email-icon" viewBox="0 0 16 16">
          <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2zm13 2.383-4.708 2.825L15 11.105V5.383zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741zM1 11.105l4.708-2.897L1 5.383v5.722z"/>
        </svg>
      </div>
      <p class="lead">We've sent a verification link to:</p>
      <p class="email-address">{{ email }}</p>
      <p class="instructions mt-3">
        Please check your email and click the verification link to complete your registration.
        You won't be able to access your account until your email is verified.
      </p>

      <div class="mt-4">
        <button @click="resendVerificationEmail" class="btn btn-outline-primary mb-3" :disabled="isResending">
          {{ isResending ? 'Sending...' : 'Resend Verification Email' }}
        </button>
      </div>

      
      <div class="mt-2">
        <button @click="$emit('show-home')" class="btn btn-link">
          Back to Home
        </button>
      </div>
      
      <!-- Display success/error messages -->
      <div v-if="message" class="alert mt-3" :class="messageType === 'success' ? 'alert-success' : 'alert-danger'">
        {{ message }}
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'CheckYourEmail',
  props: {
    email: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isResending: false,
      message: null,
      messageType: null
    };
  },
  methods: {
    async resendVerificationEmail() {
      this.isResending = true;
      this.message = null;
      
      try {
        const response = await fetch(this.$API_BASE_URL + '/api/customers/send-verification-email', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ email: this.email })
        });
        
        if (response.ok) {
          this.message = 'Verification email has been resent. Please check your inbox.';
          this.messageType = 'success';
        } else {
          const errorData = await response.json();
          this.message = `Error: ${errorData.message || 'Failed to resend verification email'}`;
          this.messageType = 'error';
        }
      } catch (error) {
        this.message = `Error: ${error.message || 'An unexpected error occurred'}`;
        this.messageType = 'error';
      } finally {
        this.isResending = false;
      }
    }
  }
};
</script>

<style scoped>
.verification-pending {
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.email-icon-container {
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
}

.email-icon {
  width: 4rem;
  height: 4rem;
  color: #0d6efd;
}

.email-address {
  font-weight: bold;
  font-size: 1.2rem;
  word-break: break-all;
}

.instructions {
  color: #6c757d;
  max-width: 450px;
  margin: 0 auto;
}

.btn-outline-primary:hover {
  transform: translateY(-2px);
  transition: all 0.3s ease;
}

/* Premium Promotion Styles */
.premium-promotion {
  margin: 2rem 0;
}

.premium-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 2rem;
  color: white;
  text-align: center;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.premium-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(102, 126, 234, 0.4);
}

.premium-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}

.premium-icon {
  font-size: 2rem;
  color: #ffd700;
  margin-right: 0.5rem;
}

.premium-title {
  color: white;
  margin: 0;
  font-weight: 600;
}

.premium-description {
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 1.5rem;
  font-size: 1rem;
  line-height: 1.5;
}

.premium-features {
  text-align: left;
  max-width: 300px;
  margin: 0 auto 1.5rem;
}

.feature-item {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  font-size: 0.95rem;
}

.feature-item i {
  margin-right: 0.75rem;
  font-size: 1.1rem;
  color: #4ade80;
}

.feature-item span {
  color: rgba(255, 255, 255, 0.95);
}

.premium-pricing {
  margin-bottom: 1.5rem;
}

.price {
  font-size: 2.5rem;
  font-weight: bold;
  color: #ffd700;
}

.period {
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
  margin-left: 0.25rem;
}

.btn-premium {
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  border: none;
  color: #1a202c;
  font-weight: 600;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 215, 0, 0.3);
}

.btn-premium:hover {
  background: linear-gradient(135deg, #ffed4e 0%, #ffd700 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 215, 0, 0.4);
  color: #1a202c;
}

.btn-premium:active {
  transform: translateY(0);
}

/* Responsive Design */
@media (max-width: 768px) {
  .verification-pending {
    padding: 1.5rem;
  }
  
  .premium-card {
    padding: 1.5rem;
  }
  
  .premium-icon {
    font-size: 1.5rem;
  }
  
  .premium-title {
    font-size: 1.25rem;
  }
  
  .price {
    font-size: 2rem;
  }
  
  .btn-premium {
    padding: 0.6rem 1.5rem;
    font-size: 1rem;
  }
}
</style>
